import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { gsap, ScrollTrigger, prefersReducedMotion } from './lib/motion';
import LogoIcon from './Logo';
import Nav from './sections/Nav';
import Footer from './sections/Footer';

function Loader({ done }: { done: boolean }) {
  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-ink-950 transition-all duration-700 ${
        done ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
    >
      <div className="text-center">
        <LogoIcon className="mx-auto h-16 w-16 animate-pulse" />
        <p className="mt-6 font-display text-sm italic tracking-wide text-steel-400">
          Built without compromise.
        </p>
      </div>
    </div>
  );
}

export default function Layout() {
  const [loaded, setLoaded] = useState(false);
  const lenisRef = useRef<Lenis | null>(null);
  const { pathname, hash } = useLocation();

  // Lenis smooth scrolling, driven by GSAP's ticker so ScrollTrigger stays in sync
  useEffect(() => {
    if (prefersReducedMotion()) return;

    const lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 1, touchMultiplier: 1.5 });
    lenisRef.current = lenis;
    lenis.on('scroll', ScrollTrigger.update);

    const tick = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Smooth same-page anchor navigation through Lenis
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a[href^="#"]') as HTMLAnchorElement | null;
      if (!a) return;
      const id = a.getAttribute('href');
      if (!id || id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      if (lenisRef.current) {
        lenisRef.current.scrollTo(target as HTMLElement, { offset: 0, duration: 1.6 });
      } else {
        target.scrollIntoView();
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // On route change: jump to top (or to the hash target), then re-measure triggers
  useEffect(() => {
    const scroll = () => {
      if (hash) {
        const target = document.querySelector(hash);
        if (target) {
          if (lenisRef.current) {
            lenisRef.current.scrollTo(target as HTMLElement, { offset: 0, duration: 1.2 });
          } else {
            target.scrollIntoView();
          }
          return;
        }
      }
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    };
    // wait a frame so the new page has rendered
    const raf = requestAnimationFrame(() => {
      scroll();
      ScrollTrigger.refresh();
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoaded(true);
      ScrollTrigger.refresh();
    }, 900);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="w-full overflow-x-hidden bg-ink-950">
      <Loader done={loaded} />
      <Nav />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
